/**
 * csSessionStore — the durable session state for the Control Surfaces workbench,
 * keyed by `projectId::branch` (the studyCursorKey idiom from useSliceState.ts).
 *
 * WHY: the V5 workbench shell unmounts a surface completely on every view switch
 * (ThreadV5Workbench builds one element per view - no keep-mounted branch), so
 * ControlSurfacesSurface used to lose its whole session - segmentation, the
 * selected surface, extracted sections, the design params, the baked cut - the
 * moment the user tabbed to another surface and back. Worse, an in-flight bake
 * poll lived in the component and was deliberately killed on unmount.
 *
 * This store lifts ALL of that state out of the component (mirroring the Study
 * Cursor store), so a view switch preserves the session and a bake started then
 * navigated-away still lands its result (startBake writes via patchCsSession
 * from module scope, exactly the patchStudyCursor imperative-write idiom).
 *
 * No persist middleware: sessions are deliberately tab-lifetime, matching
 * geometryStore's stated posture. A different projectId::branch is a different
 * byKey entry with fresh defaults; reset(key) is the user-facing "start over".
 */

import { useCallback, useEffect } from "react";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import {
  bakeCut,
  getCutJob,
  type BakeCutError,
  type BakeCutRequest,
  type ExampleGeometry,
  type SegSurface,
  type TessMesh,
} from "../../api/controlSurface";
import type { AircraftDesignSummary } from "../../api/aircraft";
import {
  getNtopBakeJob,
  startNtopBake as requestNtopBake,
  type NtopBakeRequest,
  type NtopStatusResponse,
} from "../../api/ntop";
import type { CsSections } from "../highlift/geometry/csEngine";
import {
  bakedOutputLabel,
  flattenBakeOutputs,
  pollCutJob,
  type BakedOutputRef,
} from "./controlSurfaceBake";
import { nearestFrame } from "./ntopFrames";

// ---------------------------------------------------------------------------
// Param model + status unions (moved out of the component so the store owns them)
// ---------------------------------------------------------------------------

export interface CsParams {
  eta1: number;
  eta2: number;
  /** Hinge chordwise position (x/c). */
  xc: number;
  /** Cove gap, mm. */
  gapMm: number;
  /** Cove fillet, mm. */
  filletMm: number;
  /** Hinge pivot height between lower and upper surface (0..1). */
  yh: number;
  deflectionDeg: number;
  mirror: boolean;
}

export const DEFAULT_PARAMS: CsParams = {
  eta1: 0.55,
  eta2: 0.9,
  xc: 0.72,
  gapMm: 6,
  filletMm: 0,
  yh: 0.5,
  deflectionDeg: 15,
  mirror: true,
};

export type SegStatus =
  | { status: "idle" }
  | { status: "running" }
  | { status: "done"; cached: boolean }
  | { status: "error"; error: string };

export type BakeStatus =
  | { status: "idle" }
  | { status: "running"; message?: string; jobId?: string; progress?: number }
  | { status: "done"; message: string }
  | { status: "unavailable"; message: string }
  | { status: "cancelled"; message: string }
  | { status: "error"; message: string };

/** One cached sections extraction: the spanwise contours + the STEP's unit_mm. */
export interface SectionsEntry {
  sections: CsSections;
  unitMm: number;
}

// ---------------------------------------------------------------------------
// Session shape
// ---------------------------------------------------------------------------

export interface CsSession {
  // Source
  assemblyId: string;
  stepPath: string;
  // Source picker (aggregated once per session)
  examples: ExampleGeometry[];
  examplesError: string | null;
  designs: AircraftDesignSummary[];
  designsError: string | null;
  examplesLoaded: boolean;
  // Segmentation
  segState: SegStatus;
  surfaces: SegSurface[];
  selected: SegSurface | null;
  upSign: 1 | -1;
  sectionsCache: Record<string, SectionsEntry>;
  // Editor
  params: CsParams;
  csName: string;
  csType: string;
  sectionEta: number;
  // View
  viewMode: "planform" | "3d";
  tessCache: Record<string, TessMesh>;
  stlUnits: "auto" | "mm" | "ft" | "m";
  // Bake
  bake: BakeStatus;
  bakedOutputs: BakedOutputRef[];
  /** Controls that failed to bake (timed-out / boolean error). Kept beside the
   *  successful outputs so a failed control is SHOWN ("<name> failed: ...") in
   *  the baked-pill area rather than silently vanishing from the picker. */
  bakedErrors: BakeCutError[];
  bakedIndex: number;
  meshSource: "source" | "baked";
  /** Poll generation: bumping it cancels any in-flight bake poll (a new bake, a
   *  source switch or an explicit reset) so a stale job can never land results.
   *  Shared with the nTop frame bake - either kind of newer work orphans the
   *  older poll. */
  bakeGen: number;
  // nTop parametric mode (FXC-9009): a picked partner notebook BYPASSES the
  // segment/sections pipeline - its pre-baked frame set drives the 3D pane.
  /** The ntop status (engine + notebooks) from the source aggregation, or null. */
  ntopStatus: NtopStatusResponse | null;
  /** Non-404 ntop list failure for the rack's group note (404 = feature absent). */
  ntopError: string | null;
  /** Picked notebook key; "" = not in ntop mode (the normal pipeline). */
  ntopKey: string;
  /** Frames-axis value of the ACTIVE frame (kept snapped onto the baked grid). */
  ntopFrameValue: number;
  /** The nTop frame-bake job state (same status vocabulary as `bake`). */
  ntopBake: BakeStatus;
  /** Span-fraction station (0..1) for the 2D slice action. */
  ntopSliceEta: number;
}

export function defaultCsSession(): CsSession {
  return {
    assemblyId: "",
    stepPath: "",
    examples: [],
    examplesError: null,
    designs: [],
    designsError: null,
    examplesLoaded: false,
    segState: { status: "idle" },
    surfaces: [],
    selected: null,
    upSign: 1,
    sectionsCache: {},
    params: { ...DEFAULT_PARAMS },
    csName: "aileron",
    csType: "aileron",
    sectionEta: 0.5,
    viewMode: "planform",
    tessCache: {},
    stlUnits: "auto",
    bake: { status: "idle" },
    bakedOutputs: [],
    bakedErrors: [],
    bakedIndex: 0,
    meshSource: "source",
    bakeGen: 0,
    ntopStatus: null,
    ntopError: null,
    ntopKey: "",
    ntopFrameValue: 0,
    ntopBake: { status: "idle" },
    ntopSliceEta: 0.5,
  };
}

/** Canonical session key: one CS session per project+branch. */
export function csSessionKey(projectId: string, branch: string): string {
  return `${projectId}::${branch}`;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

type CsSessionStore = {
  byKey: Record<string, CsSession>;
  /** Seed defaults for a key once (never clobbers a live session). */
  ensure: (key: string) => void;
  patch: (key: string, patch: Partial<CsSession>) => void;
  /** Back to defaults, bumping bakeGen so any in-flight poll is orphaned. */
  reset: (key: string) => void;
};

export const useCsSessionStore = create<CsSessionStore>((set) => ({
  byKey: {},
  ensure: (key) =>
    set((s) => {
      if (s.byKey[key]) return s;
      return { ...s, byKey: { ...s.byKey, [key]: defaultCsSession() } };
    }),
  patch: (key, patch) =>
    set((s) => {
      const current = s.byKey[key];
      if (!current) return s;
      return { ...s, byKey: { ...s.byKey, [key]: { ...current, ...patch } } };
    }),
  reset: (key) =>
    set((s) => {
      const next = defaultCsSession();
      next.bakeGen = (s.byKey[key]?.bakeGen ?? 0) + 1;
      return { ...s, byKey: { ...s.byKey, [key]: next } };
    }),
}));

/** Imperative, non-subscribing patch — for async work (the bake poll) that must
 *  write to the session from OUTSIDE React. No-ops until ensure() has run. */
export function patchCsSession(key: string, patch: Partial<CsSession>): void {
  useCsSessionStore.getState().patch(key, patch);
}

/** Read the live session for a key (or undefined) from module scope. */
export function csSessionState(key: string): CsSession | undefined {
  return useCsSessionStore.getState().byKey[key];
}

// A stable fallback so a pre-ensure render returns a consistent snapshot (the
// hook's effect ensures the real entry on mount; the first paint reads this).
const FALLBACK_SESSION = defaultCsSession();

export type UseCsSessionResult = {
  session: CsSession;
  patchSession: (patch: Partial<CsSession>) => void;
  sessionKey: string;
};

/**
 * The Control Surfaces session hook — one session per (projectId, branch).
 * ensure()s the entry in an effect, subscribes shallowly to the whole session
 * (top-level field references only change when patched), and hands back a stable
 * patch callback. Mirrors useStudyCursor.
 */
export function useCsSession(projectId: string, branch: string): UseCsSessionResult {
  const key = csSessionKey(projectId, branch);
  const ensure = useCsSessionStore((s) => s.ensure);
  const patch = useCsSessionStore((s) => s.patch);
  const stored = useCsSessionStore(useShallow((s) => s.byKey[key]));

  useEffect(() => {
    ensure(key);
  }, [key, ensure]);

  const session = stored ?? FALLBACK_SESSION;
  const patchSession = useCallback((p: Partial<CsSession>) => patch(key, p), [patch, key]);

  return { session, patchSession, sessionKey: key };
}

// ---------------------------------------------------------------------------
// Bake poll — module-level so it survives the component unmounting
// ---------------------------------------------------------------------------

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Answers that mean "the CAD engine is off here", not "you broke it". */
export function looksLikeFeatureOff(message: string): boolean {
  return /501|403|not implemented|not enabled|disabled|unavailable|feature/i.test(message);
}

/** Answers that mean the request was rejected as INVALID (bad params), so the
 *  user can fix it — as opposed to an engine/transport failure. */
export function looksLikeValidation(message: string): boolean {
  return (
    /\bAPI 4(?:00|22)\b/.test(message) ||
    /validation|invalid|must be|gap_mm|not a \.st|unsupported|requires/i.test(message)
  );
}

/** Drop the leading "API 4xx: " so a validation note reads as plain guidance. */
function readableDetail(message: string): string {
  return message.replace(/^API \d+:\s*/i, "").trim() || message;
}

/**
 * Start (and drive to completion) a bake cut for a session key. Runs bakeCut +
 * pollCutJob and writes every transition into the store via patchCsSession, so
 * it keeps running across a component unmount and lands bakedOutputs / bake:done
 * into the session the remounted surface renders. Generation-guarded: a source
 * switch, a new bake or a reset bumps bakeGen and this run bails silently.
 */
export async function startBake(
  key: string,
  projectId: string,
  request: BakeCutRequest,
): Promise<void> {
  const current = csSessionState(key);
  if (!current) return;
  const gen = current.bakeGen + 1;
  const superseded = () => csSessionState(key)?.bakeGen !== gen;

  patchCsSession(key, {
    bakeGen: gen,
    // Clear the previous bake's failure list up front: every terminal path that
    // carries fresh errors re-sets it, but the paths that DON'T (feature-off,
    // no-job-id accepted, a thrown submit/network error below) would otherwise
    // leave a stale chip misattributing the LAST bake's failures to this one.
    bakedErrors: [],
    bake: { status: "running", message: "Submitting the cut..." },
  });

  try {
    const res = await bakeCut(projectId, request);
    if (superseded()) return;
    if (res.feature_enabled === false) {
      patchCsSession(key, {
        bake: {
          status: "unavailable",
          message:
            "The CAD cut engine is not enabled in this environment - the request was staged but no geometry was cut.",
        },
      });
      return;
    }
    if (!res.job_id) {
      patchCsSession(key, { bake: { status: "done", message: "Cut request accepted." } });
      return;
    }

    const jobId = res.job_id;
    patchCsSession(key, {
      bake: { status: "running", message: "Cutting...", jobId, progress: 0 },
    });

    const status = await pollCutJob(() => getCutJob(projectId, jobId), {
      isCancelled: superseded,
      onProgress: (s) =>
        patchCsSession(key, {
          bake: { status: "running", message: s.message || "Cutting...", jobId, progress: s.progress },
        }),
    });
    if (!status || superseded()) return; // locally cancelled / superseded

    if (status.status === "cancelled") {
      const kept = flattenBakeOutputs(status.result);
      const patch: Partial<CsSession> = {
        // Surface the failures too, so a control cut short by the cancel is
        // shown in the baked-pill area rather than vanishing.
        bakedErrors: status.result?.errors ?? [],
        bake: {
          status: "cancelled",
          message: kept.length
            ? `Cut cancelled - kept ${kept.length} finished cut(s).`
            : "Cut cancelled before any geometry was produced.",
        },
      };
      // Surface whatever DID finish before the cancel landed.
      if (kept.length) {
        patch.bakedOutputs = kept;
        patch.bakedIndex = 0;
        patch.meshSource = "baked";
        patch.viewMode = "3d";
      }
      patchCsSession(key, patch);
      return;
    }

    if (status.status === "error") {
      patchCsSession(key, {
        bakedErrors: status.result?.errors ?? [],
        bake: { status: "error", message: status.error || "Cut job failed." },
      });
      return;
    }

    const outputs = flattenBakeOutputs(status.result);
    const failures = status.result?.errors ?? [];
    if (outputs.length === 0) {
      patchCsSession(key, {
        bakedErrors: failures,
        bake: {
          status: "error",
          message: failures[0]
            ? `${failures[0].name}: ${failures[0].error}`
            : "The cut completed but produced no output geometry.",
        },
      });
      return;
    }

    // Show the real cut: baked auto-selected, 3D pane brought forward. Any
    // controls that FAILED ride alongside in bakedErrors (surfaced in the pill
    // area) so a timed-out control never silently vanishes from the picker.
    patchCsSession(key, {
      bakedOutputs: outputs,
      bakedErrors: failures,
      bakedIndex: 0,
      meshSource: "baked",
      viewMode: "3d",
      bake: {
        status: "done",
        message:
          failures.length > 0
            ? `Baked ${outputs.length} cut(s); ${failures.length} failed - ${failures[0]!.name}: ${failures[0]!.error}`
            : `Baked ${outputs.length} cut(s) - showing ${bakedOutputLabel(outputs[0]!)}.`,
      },
    });
  } catch (e) {
    if (superseded()) return; // superseded - drop the stale error
    const message = errorMessage(e);
    if (looksLikeFeatureOff(message)) {
      patchCsSession(key, {
        bake: {
          status: "unavailable",
          message:
            "The CAD cut engine is not enabled in this environment - live 2D design, variants and section solves still work.",
        },
      });
    } else if (looksLikeValidation(message)) {
      patchCsSession(key, {
        bake: {
          status: "error",
          message: `The cut request was rejected - ${readableDetail(message)}`,
        },
      });
    } else {
      patchCsSession(key, { bake: { status: "error", message } });
    }
  }
}

// ---------------------------------------------------------------------------
// nTop frame bake - startBake's sibling, module-level for the same reason
// ---------------------------------------------------------------------------

/** The honest engine-off note (also shown by the panel's bake button). */
export const NTOP_ENGINE_OFF_NOTE =
  "nTopCL is not configured in this environment - drop pre-baked frames in the nTop dir or set THREAD_NTOP_CL_*";

/**
 * Start (and drive to completion) an nTop FRAME bake for a session key.
 * Mirrors startBake exactly: bakeGen generation guard (a source switch, a
 * newer bake or a reset orphans this poll), every transition patched into
 * `ntopBake` via patchCsSession so the run survives the component unmounting,
 * and pollCutJob reused verbatim (the bake job route returns the same status
 * shape). On complete the fresh frames MANIFEST is written onto the notebook
 * in ntopStatus and the active frame value snaps to the nearest baked value,
 * so the viewer lands on a real frame.
 */
export async function startNtopBake(
  key: string,
  projectId: string,
  request: NtopBakeRequest,
): Promise<void> {
  const current = csSessionState(key);
  if (!current) return;
  const gen = current.bakeGen + 1;
  const superseded = () => csSessionState(key)?.bakeGen !== gen;

  patchCsSession(key, {
    bakeGen: gen,
    ntopBake: { status: "running", message: "Submitting the frame bake..." },
  });

  try {
    const res = await requestNtopBake(projectId, request);
    if (superseded()) return;
    if (res.feature_enabled === false) {
      patchCsSession(key, {
        ntopBake: { status: "unavailable", message: res.note || NTOP_ENGINE_OFF_NOTE },
      });
      return;
    }
    if (!res.job_id) {
      patchCsSession(key, { ntopBake: { status: "done", message: "Bake request accepted." } });
      return;
    }

    const jobId = res.job_id;
    patchCsSession(key, {
      ntopBake: { status: "running", message: "Baking frames...", jobId, progress: 0 },
    });

    const status = await pollCutJob(() => getNtopBakeJob(projectId, jobId), {
      timeoutMs: Number.POSITIVE_INFINITY,
      isCancelled: superseded,
      onProgress: (s) =>
        patchCsSession(key, {
          ntopBake: {
            status: "running",
            message: s.message || "Baking frames...",
            jobId,
            progress: s.progress,
          },
        }),
    });
    if (!status || superseded()) return; // locally cancelled / superseded

    const manifest = status.result;
    if (status.status === "cancelled") {
      patchCsSession(key, {
        ntopBake: {
          status: "cancelled",
          message: "Bake cancelled - the existing frame set is unchanged.",
        },
      });
      return;
    }
    if (status.status === "error") {
      patchCsSession(key, {
        ntopBake: { status: "error", message: status.error || "Frame bake failed." },
      });
      return;
    }

    if (!manifest || manifest.frames.length === 0) {
      patchCsSession(key, {
        ntopBake: {
          status: "error",
          message: "The bake completed but produced no frames.",
        },
      });
      return;
    }

    // Land the fresh manifest on the notebook it belongs to (read the LIVE
    // session - the user may have scrubbed/patched during the bake) and snap
    // the active frame value onto the new grid.
    const live = csSessionState(key);
    const ntopStatus = live?.ntopStatus;
    const values = manifest.frames.map((f) => f.value).filter((v) => Number.isFinite(v));
    const frameKeys = new Set(manifest.frames.map((frame) => `step:${frame.stl}`));
    patchCsSession(key, {
      ...(ntopStatus
        ? {
            ntopStatus: {
              ...ntopStatus,
              notebooks: ntopStatus.notebooks.map((nb) =>
                nb.key === request.notebookKey ? { ...nb, frames: manifest } : nb,
              ),
            },
          }
        : {}),
      tessCache: Object.fromEntries(
        Object.entries(live?.tessCache ?? {}).filter(([cacheKey]) => !frameKeys.has(cacheKey)),
      ),
      ntopFrameValue: nearestFrame(values, live?.ntopFrameValue ?? 0),
      ntopBake: {
        status: "done",
        message: `Baked ${manifest.frames.length} frame(s) along ${manifest.axis}.`,
      },
    });
  } catch (e) {
    if (superseded()) return; // superseded - drop the stale error
    const message = errorMessage(e);
    if (looksLikeFeatureOff(message)) {
      patchCsSession(key, {
        ntopBake: { status: "unavailable", message: NTOP_ENGINE_OFF_NOTE },
      });
    } else if (looksLikeValidation(message)) {
      patchCsSession(key, {
        ntopBake: {
          status: "error",
          message: `The bake request was rejected - ${readableDetail(message)}`,
        },
      });
    } else {
      patchCsSession(key, { ntopBake: { status: "error", message } });
    }
  }
}

// ---------------------------------------------------------------------------
// Test seam
// ---------------------------------------------------------------------------

function resetCsSessionStore(): void {
  useCsSessionStore.setState({ byKey: {} });
}

export const __internal = { useCsSessionStore, resetCsSessionStore, defaultCsSession };
